/**
 * The House Guide search box (docs/GUEST-REDESIGN.md Section 5, item 3): a
 * plain text search over the whole guide that works offline because every
 * word is already in the bundle. Kept pure and small on purpose, the way
 * `logic/rooms.ts` and `logic/thread.ts` do it: a screen supplies the data it
 * already loaded, this file only decides what matches.
 */

export type SearchTarget =
  | { kind: 'route'; to: string }
  | { kind: 'anchor'; id: string };

export type SearchableDoc = {
  key: string;
  title: string;
  lede?: string;
  body: string;
  target: SearchTarget;
};

export type SearchHit = {
  key: string;
  title: string;
  snippet: string;
  target: SearchTarget;
};

function normalize(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    // "Wi-Fi" should answer to "wifi": a hyphen is punctuation, not a sound.
    .replace(/-/g, '');
}

/** A short run of plain words around the first match, for the result line. */
function snippetAround(haystack: string, needleNormalized: string): string {
  const plain = haystack.replace(/[#*_>`[\]()]/g, ' ').replace(/\s+/g, ' ').trim();
  const at = normalize(plain).indexOf(needleNormalized);
  if (at === -1) return plain.slice(0, 96);
  const start = Math.max(0, at - 40);
  const end = Math.min(plain.length, at + needleNormalized.length + 60);
  return `${start > 0 ? '… ' : ''}${plain.slice(start, end)}${end < plain.length ? ' …' : ''}`;
}

/**
 * Case and accent insensitive substring search across a title, its lede and
 * its body. A title match sorts first, so "hacienda" finds the hacienda page
 * before a page that only mentions the word once in passing.
 */
export function searchGuide(docs: SearchableDoc[], query: string): SearchHit[] {
  const q = normalize(query.trim());
  if (!q) return [];

  const hits: Array<{ hit: SearchHit; titleMatch: boolean }> = [];
  for (const doc of docs) {
    const titleMatch = normalize(doc.title).includes(q);
    const ledeMatch = doc.lede ? normalize(doc.lede).includes(q) : false;
    const bodyMatch = normalize(doc.body).includes(q);
    if (!titleMatch && !ledeMatch && !bodyMatch) continue;

    const snippetSource = ledeMatch && doc.lede ? doc.lede : doc.body;
    hits.push({
      hit: {
        key: doc.key,
        title: doc.title,
        snippet: titleMatch && !ledeMatch && !bodyMatch ? (doc.lede ?? '') : snippetAround(snippetSource, q),
        target: doc.target,
      },
      titleMatch,
    });
  }

  hits.sort((a, b) => Number(b.titleMatch) - Number(a.titleMatch));
  return hits.map((h) => h.hit);
}
